import { useState } from 'react';
import { type CareerProfile } from 'CareerProfiles';
import { getProgramConfigValue } from 'Program';
import { useTranslation } from 'react-i18next';
import { getAvatarColor } from '../../utils/avatarColor';
import { ProgramBadges } from './ProgramBadges';
import '../../locales/studentNetworkV2/studentNetworkV2-en.json';

type ProfileCardProps = {
    profile: CareerProfile;
    onClick?: () => void;
    onMessage?: () => void;
};

type ProfileCardPresentationProps = {
    profileId: string;
    displayName: string;
    companyRole: string;
    location: string;
    educationLines: string[];
    programBadges: { branding: string; label: string }[];
    avatarBg: string;
    avatarUrl: string | null;
    initials: string;
    showImg: boolean;
    isAnonymized: boolean;
    onImgError: () => void;
    onClick?: () => void;
    onMessage?: () => void;
};

// oxlint-disable-next-line max-lines-per-function -- presentation component with mobile/desktop layouts
export function ProfileCardPresentation({
    profileId,
    displayName,
    companyRole,
    location,
    educationLines,
    programBadges,
    avatarBg,
    avatarUrl,
    initials,
    showImg,
    isAnonymized,
    onImgError,
    onClick,
    onMessage,
}: ProfileCardPresentationProps) {
    const { t } = useTranslation('back_royal', { keyPrefix: 'studentNetworkV2.studentNetworkV2.people' });

    const avatar = (
        <div
            className="flex h-[50px] w-[50px] shrink-0 items-center justify-center overflow-hidden rounded-full"
            style={{ backgroundColor: avatarBg }}
        >
            {showImg ? (
                <img src={avatarUrl!} alt="" className="h-full w-full object-cover" onError={onImgError} />
            ) : (
                <span className="text-[22px] font-bold text-white">{initials}</span>
            )}
        </div>
    );

    return (
        <div
            role="button"
            tabIndex={0}
            onClick={onClick}
            onKeyDown={e => e.key === 'Enter' && onClick?.()}
            className="w-full cursor-pointer text-start"
            data-career-profile-id={profileId}
        >
            {/* Mobile layout: avatar left, content right, message button bottom-right */}
            <div className="flex gap-4 px-5 py-4 md:hidden">
                {avatar}
                <div className="min-w-0 flex-1">
                    {/* Name */}
                    <div className="text-base font-bold text-black">{displayName}</div>

                    {/* Company / role */}
                    {companyRole && <div className="truncate text-base text-black">{companyRole}</div>}

                    {/* Location + program badges + message button row */}
                    <div className="flex items-end gap-2">
                        <div className="min-w-0 flex-1">
                            {location && <div className="text-base text-slate-grey">{location}</div>}
                            {!isAnonymized && <ProgramBadges badges={programBadges} className="overflow-hidden" />}
                        </div>
                        {!isAnonymized && onMessage && (
                            <button
                                type="button"
                                className="h-9 shrink-0 rounded-full bg-black px-4 text-base font-normal text-white hover:bg-black/80"
                                onClick={e => {
                                    e.stopPropagation();
                                    onMessage();
                                }}
                            >
                                {t('message')}
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Desktop layout: avatar left, content center, message button top-right */}
            <div className="hidden gap-4 px-5 py-5 md:flex">
                {avatar}
                <div className="min-w-0 flex-1">
                    {/* Name */}
                    <div className="text-base font-bold text-black">{displayName}</div>

                    {/* Company / role */}
                    {companyRole && <div className="text-base text-black">{companyRole}</div>}

                    {/* Location */}
                    {location && <div className="text-base text-slate-grey">{location}</div>}

                    {/* Program badges */}
                    {!isAnonymized && <ProgramBadges badges={programBadges} className="overflow-hidden" />}

                    {/* Divider + Education — desktop only */}
                    <div className="my-2 h-px bg-slate-grey-200" />
                    {educationLines.slice(0, 3).map(line => (
                        <div key={line} className="ps-4 -indent-4 text-base text-slate-grey">
                            {line}
                        </div>
                    ))}
                </div>

                {/* Message button — top-right, white with border */}
                {!isAnonymized && onMessage && (
                    <div className="shrink-0 pt-1.5">
                        <button
                            type="button"
                            className="h-9 rounded-full border border-slate-grey-200 bg-white px-4 text-base font-normal text-black hover:bg-slate-grey-100-alt"
                            onClick={e => {
                                e.stopPropagation();
                                onMessage();
                            }}
                        >
                            {t('message')}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

// oxlint-disable-next-line max-lines-per-function -- data derivation wrapper
export function ProfileCard({ profile, onClick, onMessage }: ProfileCardProps) {
    const { t: tDegree } = useTranslation('back_royal', {
        keyPrefix: 'careers.edit_career_profile.education_experience_detail',
    });

    const isAnonymized =
        (profile as Record<string, unknown>).anonymized === true || profile.prefStudentNetworkPrivacy === 'anonymous';

    const displayName = isAnonymized ? (profile.nickname ?? '?') : (profile.name ?? '');
    const jobTitle = profile.workExperiences?.[0]?.jobTitle ?? '';
    const company =
        profile.workExperiences?.[0]?.professionalOrganization?.text ??
        profile.workExperiences?.[0]?.professional_organization?.text ??
        '';
    const companyRole = [jobTitle, company].filter(Boolean).join(', ');
    const location = [profile.city, profile.state, profile.country].filter(Boolean).join(', ');

    const programLines = (profile.programs ?? []).map(p => p.cohortTitle);

    type EduExperience = {
        degreeProgram?: boolean;
        degree?: string;
        degreeName?: string;
        major?: string;
        educationalOrganization?: { text?: string };
    };
    const rawEdu = (profile as Record<string, unknown>).educationExperiences as EduExperience[] | undefined;

    const externalEduLines = (rawEdu ?? [])
        .filter(
            e =>
                e.degreeProgram &&
                e.educationalOrganization?.text &&
                !e.educationalOrganization.text.toLowerCase().includes('quantic') &&
                !e.educationalOrganization.text.toLowerCase().includes('smartly'),
        )
        .map(e => {
            const degreeKey = e.degree ?? '';
            const degreeName = tDegree(degreeKey, { defaultValue: degreeKey });
            return [degreeName, e.major, e.educationalOrganization?.text].filter(Boolean).join(', ');
        });

    const educationLines = [...programLines, ...externalEduLines];

    const seen = new Set<string>();
    const programBadges = (profile.programs ?? [])
        .map(program => ({
            branding: program.institutionBranding,
            label: getProgramConfigValue(program.programType, 'shortProgramTitle') as string,
        }))
        .filter(b => {
            if (!b.label) return false;
            const key = `${b.branding}-${b.label}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });

    const avatarBg = getAvatarColor(profile.id);
    const [imgFailed, setImgFailed] = useState(false);
    const showImg = !!profile.avatarUrl && !isAnonymized && !imgFailed;
    const initials = (displayName || '?')
        .split(' ')
        .map(n => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);

    return (
        <ProfileCardPresentation
            profileId={profile.id}
            displayName={displayName}
            companyRole={companyRole}
            location={location}
            educationLines={educationLines}
            programBadges={programBadges}
            avatarBg={avatarBg}
            avatarUrl={profile.avatarUrl}
            initials={initials}
            showImg={showImg}
            isAnonymized={isAnonymized}
            onImgError={() => setImgFailed(true)}
            onClick={onClick}
            onMessage={onMessage}
        />
    );
}
